import React, {
  useEffect,
  useState,
  createContext,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  getCompany,
  getUserCompanies,
  getActionItems,
  getLocations,
  getReleaseNotes,
  getDomains,
} from "../../shared/common.api.js";
import { toasterService } from "../toaster/toaster-service.js";
import { DateTime } from "luxon";
import { api } from "../../shared/api";
import { getMe, logOut as logOutApi } from "../../shared/auth.api";
import Cookies from "js-cookie";

import CONSTANTS from "../../shared/constants.js";

// Keys for localstorage items.
const TOKEN = "cinch.userToken";
const TOKEN_EXPIRY = "cinch.userTokenExpires";
const AS_COMPANY = "cinch.asCompany";

export const UserStateContext = createContext();

export const UserStateContextProvider = ({ children }) => {
  const navigate = useNavigate();
  // Global state items

  /**
   * Remembers the last page we were on and redirects there after a successful log in.
   *
   * @param {string} redirect
   */
  const [redirect, setRedirect] = useState("/dashboard");

  /**
   * Remembers the last page we were on and redirects there after a successful log in.
   *
   * @param {string} redirect
   */
  const [needsMFA, setNeedsMFA] = useState(false);

  /**
   * Company object that holds the current company we're "Working As". Sometimes it may only
   * contain the `id` key before it gets loaded with company information from an API call.
   *
   * By default we first check the URL's `?company=` query param for the company to start with.
   * If there isn't a company in the query param then check if we saved one in the local storage.
   *
   * @param {Object} asCompany
   */
  const [asCompany, setAsCompany] = useState(() => {
    // First check if we're setting the company ID from the URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    let companyId = urlParams.get("company");

    if (!companyId) {
      // If we don't have a company ID in the query params check browser storage
      companyId = Cookies.get(AS_COMPANY);
    }

    if (companyId) {
      // If we found a company ID set it to the state and our `useEffect` will load it..
      return {
        id: companyId,
      };
    }
  });

  /**
   * Token to use in the API.
   *
   * By default this is first loaded from the query parm `?authentication=` but if there
   * isn't one in the query params we then load it from local starge if the token expiration
   * is in the future.
   *
   * @param {string} token
   */
  const [token, setTokenState] = useState(() => {
    // First check for token in the query parameters
    const searchParams = new URLSearchParams(window.location.search);
    const authenticationParam = searchParams.get("authentication");

    if (authenticationParam) {
      const r = setRedirect(
        (window.location.pathname || "/dashboard") + "?" + searchParams
      );

      return authenticationParam;
    }

    // Next check for token in storage
    const token = Cookies.get(TOKEN);
    const tokenExpiry = Cookies.get(TOKEN_EXPIRY);

    const notExpired =
      DateTime.fromISO(tokenExpiry).toMillis() -
        DateTime.local().toUTC().toMillis() >
      0;
    if (notExpired) {
      // If the token is not expired we assume it's still valid though we may find out otherwise shortly.
      return token;
    }
  });

  const setToken = (token) => {
    setTokenState(token);
    tokenRef.current = token;
  };

  /**
   * Holds the token in a ref so we can use it in the interceptor before it is used in the useEffect(s).

   * @param {string} tokenRef
   **/

  const tokenRef = useRef(token);

  /**
   * Holds the token's expiration date so we can save ourselves the trouble of using an expired token.
   *
   * @param {string} tokenExpiry
   */
  const [tokenExpiry, setTokenExpiry] = useState(() => {
    // Check for token in storage.
    const tokenExpiry = Cookies.get(TOKEN_EXPIRY);

    const notExpired =
      DateTime.fromISO(tokenExpiry).toMillis() -
        DateTime.local().toUTC().toMillis() >
      0;
    if (notExpired) {
      // If the token is not expired we assume it's still valid though we may find out otherwise shortly.
      return tokenExpiry;
    }
  });

  /**
   * When `undefined` that means we haven't loaded a user yet and we're still in a loading state.
   * When `null` that means we're loaded and we know that we aren't logged in nor have a valid user.
   * When `Object` that means we're logged in and have a user.
   *
   * @param {undefined|null|Object} user User object that contains the currently logged in user's data.
   */
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const passToken = (config) => {
      if (config.url !== "/users/login") {
        // const token = Cookies.get(TOKEN);
        const token = tokenRef.current;
        if (token) {
          config.headers["Authorization"] = `Token ${token}`;
        }
      }

      return config;
    };

    // Pass tokens for all requests
    api.interceptors.request.use(passToken);

    const checkResponseSuccess = (res) => res;
    const checkResponseFailure = (err) => {
      // If we need MFA redirect there.
      if (
        err?.response?.status === 401 &&
        err?.response?.data?.detail ===
          "Token needs to be authenticated with MFA"
      ) {
        setNeedsMFA(true);
        if (window.location.pathname !== "/mfa") {
          // Preserve the current location as redirect parameter for MFA
          const currentPath = window.location.pathname + window.location.search;

          navigate(`/mfa?redirect=${encodeURIComponent(currentPath)}`);
        }

        return "Token needs to be authenticated with MFA";
      }

      if (err.response && err.response.status === 401) {
        if (err.response.config.url === "/users/logout") {
          navigate("/sign-in");
          return;
        } else if (err.response.config.url !== "/users/me") {
          // Check me
          return getMe().then(({ status }) => {
            if (status != 200) {
              toasterService.warning("Session timed out - please sign in.");
              logOut();
              return err.response;
            }

            // We had a different problem
            return Promise.reject(err.response);
          });
        } else {
          logOut();
          return;
        }
      }

      return Promise.reject(err);
    };

    // Check if our token expired.
    api.interceptors.response.use(checkResponseSuccess, checkResponseFailure);

    return () => {
      api.interceptors.request.eject(passToken);
      api.interceptors.response.eject(
        checkResponseSuccess,
        checkResponseFailure
      );
    };
  }, []);

  const loadUser = () => {
    getMe().then(({ data: user }) => {
      setUser(user);
      if (user?.color_preference || user?.color_preference_secondary) {
        const root = document.documentElement;
        root?.style.setProperty(
          "--custom-background-color",
          user?.color_preference || "#e4e9f1"
        );
        root?.style.setProperty(
          "--custom-background-color-secondary",
          user?.color_preference_secondary || "#ffffff"
        );
      }
    });
  };

  /**
   * When the token changes we need to do the following it:
   * - Add or remove token from local storage depending on if we have a token or not.
   * - Update our API calls to use the new token
   * - Get user info from API and sets it on state
   *
   * @listens token
   * @fires user
   */
  useEffect(() => {
    if (!token) {
      try {
        localStorage.removeItem(TOKEN);
        if (CONSTANTS.ENV === "LOCAL") {
          Cookies.remove(TOKEN);
        } else if (CONSTANTS.ENV === "DEV") {
          Cookies.remove(TOKEN, { domain: ".marketing-dev.cinch.io" });
        } else {
          Cookies.remove(TOKEN, { domain: ".marketing.cinch.io" });
        }
      } catch (e) {
        console.log("Local storage is not writable.");
      }
      setUser(false);
      return;
    }

    if (CONSTANTS.ENV === "LOCAL") {
      Cookies.set(TOKEN, token, { expires: 365, sameSite: "strict" });
    } else if (CONSTANTS.ENV === "DEV") {
      Cookies.set(TOKEN, token, {
        domain: ".marketing-dev.cinch.io",
        expires: 365,
        sameSite: "strict",
        secure: true,
      });
    } else {
      Cookies.set(TOKEN, token, {
        domain: ".marketing.cinch.io",
        expires: 365,
        sameSite: "strict",
        secure: true,
      });
    }
    setUser(undefined);
    if (!needsMFA) {
      loadUser();
    }
  }, [token, needsMFA]);

  /**
   * Keep local storage in sync with tokenExpiry
   *
   * @listens tokenExpiry
   */
  useEffect(() => {
    if (!tokenExpiry) {
      try {
        localStorage.removeItem(TOKEN_EXPIRY);

        if (CONSTANTS.ENV === "LOCAL") {
          Cookies.remove(TOKEN_EXPIRY);
        } else if (CONSTANTS.ENV === "DEV") {
          Cookies.remove(TOKEN_EXPIRY, { domain: ".marketing-dev.cinch.io" });
        } else {
          Cookies.remove(TOKEN_EXPIRY, { domain: ".marketing.cinch.io" });
        }
      } catch (e) {
        console.log("Local storage is not writable.");
      }

      return;
    }

    if (CONSTANTS.ENV === "LOCAL") {
      Cookies.set(TOKEN_EXPIRY, tokenExpiry, {
        expires: 365,
        sameSite: "strict",
      });
    } else if (CONSTANTS.ENV === "DEV") {
      Cookies.set(TOKEN_EXPIRY, tokenExpiry, {
        domain: ".marketing-dev.cinch.io",
        expires: 365,
        sameSite: "strict",
        secure: true,
      });
    } else {
      Cookies.set(TOKEN_EXPIRY, tokenExpiry, {
        domain: ".marketing.cinch.io",
        expires: 365,
        sameSite: "strict",
        secure: true,
      });
    }
  }, [tokenExpiry]);

  /**
   * Keep local storage in sync with current "working as" company
   *
   * @listens asCompany.id
   */
  useEffect(() => {
    if (!asCompany?.id) {
      if (CONSTANTS.ENV === "LOCAL") {
        Cookies.remove(AS_COMPANY);
      } else if (CONSTANTS.ENV === "DEV") {
        Cookies.remove(AS_COMPANY, { domain: ".marketing-dev.cinch.io" });
      } else {
        Cookies.remove(AS_COMPANY, { domain: ".marketing.cinch.io" });
      }
      return;
    }

    if (CONSTANTS.ENV === "LOCAL") {
      Cookies.set(AS_COMPANY, asCompany.id, {
        expires: 365,
        sameSite: "strict",
      });
    } else if (CONSTANTS.ENV === "DEV") {
      Cookies.set(AS_COMPANY, asCompany.id, {
        domain: ".marketing-dev.cinch.io",
        expires: 365,
        sameSite: "strict",
        secure: true,
      });
    } else {
      Cookies.set(AS_COMPANY, asCompany.id, {
        domain: ".marketing.cinch.io",
        expires: 365,
        sameSite: "strict",
        secure: true,
      });
    }
  }, [asCompany?.id]);

  /**
   * If we don't have asCompany data loaded get it from the API
   *
   * @listens user
   * @listens asCompany.id
   * @fires asCompany      !Warning may cause circular triggers
   */
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    if (!user?.id || !!asCompany?.is_active) return;

    let asCompanyId = asCompany?.id;

    if (
      (!asCompanyId && user.companies.length > 0) ||
      (!user.is_superuser &&
        !user.companies.find((company) => company.id === asCompany?.id))
    ) {
      // If we don't have a company ID load the user's first company.
      // If we do have a company ID but this user doesn't have access to it, then also load the user's first company.
      if (user?.companies?.length === 0) {
        // This user has no companies.
        setAsCompany(null);
        return;
      }

      asCompanyId = user?.companies[0]?.id;
    }

    if (asCompanyId) {
      const promises = [getCompany(asCompanyId), getUserCompanies(user.id)];
      Promise.all(promises).then(([company, roles]) => {
        setUserRole(
          roles?.data?.results?.find((role) => role?.company == asCompanyId) ||
            roles?.data?.results[0]
        );

        setAsCompany(company.data);
      });
    }
  }, [user, asCompany?.id]);

  /**
   * Has Permission.
   *
   * Check if the user has at leaset one of these permissions for the current company.
   *
   * hasPermission('permission1')
   * hasPermission('permission1', 'permission2')
   *
   * @param {string} permission... One or more permissions to check.
   *
   * @return {boolean} Whether or not this user has permission to at least one of these permissions for the "working as" company.
   */
  const hasPermission = useCallback(
    (...permissions) => {
      if (!user || !user.permissions || !asCompany.id) {
        return false;
      }

      return permissions.some((permission) => {
        return (
          !!user.permissions["*"] ||
          (user.permissions[permission] &&
            user.permissions[permission].indexOf(asCompany.id) > -1)
        );
      });
    },
    [user?.permissions, asCompany?.id]
  );

  /**
   * Has Multi Permission.
   *
   * Check if the user has permission to more than one company
   *
   * hasMultiPermission('permission1')
   *
   * @param {string} permission Permission to check.
   *
   * @return {boolean} Whether or not this user has permission to multiple companies
   */
  const hasMultiPermission = useCallback(
    (permission) => {
      if (!user || !user?.permissions || !asCompany?.id) {
        return false;
      }

      return (
        !!user?.permissions["*"] || user?.permissions[permission]?.length > 1
      );
    },
    [user?.permissions, asCompany?.id]
  );

  /**
   * Log Out
   *
   * Logs out the current user's session.  The `useEffect` functions will make sure this persists in local storage. Also redirect to
   * the `sign-in` page.
   */
  const logOut = () => {
    // Log out in API
    logOutApi()
      .catch(
        // catch they were forcefully logged out
        (err) => {
          if (err.response && err.response.status === 401) {
          }
        }
      )
      .then(() => {
        const MYLOCAL = "https://fe.krichey.vm.cinch.io";
        const forwardingTarget =
          CONSTANTS.ENV === "LOCAL"
            ? MYLOCAL
            : CONSTANTS.ENV === "DEV"
            ? "https://marketing-dev.cinch.io"
            : "https://marketing.cinch.io";

        window.location.replace(forwardingTarget + "/logout");
      });
  };

  /**
   * Has Multi Access.
   *
   * Check if the user has access to more than one company. This can be used to show whether or not the user can see the "working as" dropdown.
   *
   * @return {boolean} Whether or not this user has access to more than one company.
   */
  const hasMultiAccess = () => {
    return user.companies.length > 1;
  };

  const isValidToken = () => {
    return (
      DateTime.fromISO(tokenExpiry).toMillis() -
        DateTime.local().toUTC().toMillis() >
      0
    );
  };

  const refreshUserData = () => {
    if (!user?.id || !asCompany?.id) {
      return;
    }

    loadUser();
  };

  return (
    <UserStateContext.Provider
      value={{
        asCompany,
        user,
        redirect,
        token,
        hasPermission,
        hasMultiPermission,
        logOut,
        setAsCompany,
        setToken,
        setTokenExpiry,
        hasMultiAccess,
        setUser,
        setRedirect,
        isValidToken,
        needsMFA,
        setNeedsMFA,
        userRole,
        refreshUserData,
      }}
    >
      {children}
    </UserStateContext.Provider>
  );
};
